










































































































import Vue from "vue";
import dayjs from "dayjs";
import eventBus from "@/services/eventBus";
import DateInput from "@/components/DateInput.vue";
import CheckBox from "@/components/CheckBox.vue";
import Tabs from "@/components/Tabs.vue";
import api from "../services/api";
import { LedgerExportLookup, LedgerExportRequest } from "../models/LedgerExport";

// K2D "Export és import -> Főkönyvi export" (m_dun_exp / w_popup_datuminput) portja.
// Aggregált egyenleg-export accno × dimcode1 bontásban, TAB-os TXT-be. A négy kijelölő-fül a
// K2D treff-jelölős gridjeinek felel meg; a keresőmező a K2D-hez hasonlóan a kijelölt sorokat
// mindig mutatja. A fájlt a backend Base64-ben adja vissza, a letöltés data-URL-lel történik
// (BalanceIncomeReportView exportExcel minta).

interface ExportTab {
  key: "accnos" | "dimCodes1" | "dimCodes2" | "dimCodes3";
  title: string;
  search: string;
  skip: number;
  take: number;
  items: LedgerExportLookup[];
}

const PAGE_SIZE = 50;

interface Data {
  dateFrom: Date | null;
  dateTo: Date | null;
  includeStorno: boolean;
  tabs: ExportTab[];
  columns: object[];
}

interface Methods {
  filteredItems(tab: ExportTab): LedgerExportLookup[];
  pagedItems(tab: ExportTab): LedgerExportLookup[];
  onPageChange(tab: ExportTab, event: { page: { skip: number; take: number } }): void;
  selectedCount(tab: ExportTab): number;
  selectedCodes(key: ExportTab["key"]): string[];
  toggleItem(item: LedgerExportLookup): void;
  onRowClick(event: { dataItem: LedgerExportLookup }): void;
  selectAllInTab(tab: ExportTab): void;
  clearTab(tab: ExportTab): void;
  resetForm(): void;
  exportFile(): Promise<void>;
}

export default Vue.extend<Data, Methods, {}, {}>({
  name: "LedgerExportView",

  components: { DateInput, CheckBox, Tabs },

  data(): Data {
    return {
      // K2D open: alapértelmezett tartomány az aktuális hónap
      dateFrom: dayjs().startOf("month").toDate(),
      dateTo: dayjs().endOf("month").toDate(),
      includeStorno: false,
      tabs: [
        { key: "accnos", title: "Főkönyvi szám", search: "", skip: 0, take: PAGE_SIZE, items: [] },
        { key: "dimCodes1", title: "Költséghely / Dimkód1", search: "", skip: 0, take: PAGE_SIZE, items: [] },
        { key: "dimCodes2", title: "Telephely / Dimkód2", search: "", skip: 0, take: PAGE_SIZE, items: [] },
        { key: "dimCodes3", title: "Partner / Dimkód3", search: "", skip: 0, take: PAGE_SIZE, items: [] },
      ],
      columns: [
        { cell: "selectCell", title: " ", width: "60px", headerClassName: "center_text", filterable: false, sortable: false },
        { field: "code", title: "Kód", width: "160px" },
        { field: "name", title: "Megnevezés" },
      ],
    };
  },

  async beforeMount() {
    eventBus.$emit("busy-message", true);
    try {
      const [accounts, dims1, dims2, dims3] = await Promise.all([
        api.GetLedgerExportAccounts(),
        api.GetLedgerExportDimCodes(2),
        api.GetLedgerExportDimCodes(3),
        api.GetLedgerExportDimCodes(4),
      ]);
      this.tabs[0].items = accounts.map((x) => ({ ...x, selected: false }));
      this.tabs[1].items = dims1.map((x) => ({ ...x, selected: false }));
      this.tabs[2].items = dims2.map((x) => ({ ...x, selected: false }));
      this.tabs[3].items = dims3.map((x) => ({ ...x, selected: false }));
    } catch (e) {
      console.error(e);
      eventBus.$emit("error", "A kijelölő listák betöltése sikertelen!");
    } finally {
      eventBus.$emit("busy-message", false);
    }
  },

  methods: {
    // K2D ue_edit_enchance: a kijelölt sorok a szűréstől függetlenül látszanak,
    // a keresés a kódra (prefix) és a megnevezésre (tartalmazás) illeszt.
    filteredItems(tab: ExportTab): LedgerExportLookup[] {
      const search = (tab.search || "").trim().toLowerCase();
      if (!search) return tab.items;
      return tab.items.filter(
        (i) =>
          i.selected ||
          (i.code || "").toLowerCase().startsWith(search) ||
          (i.name || "").toLowerCase().includes(search)
      );
    },

    // A grid csak az aktuális lapot rendereli (accno-lista több ezer soros lehet)
    pagedItems(tab: ExportTab): LedgerExportLookup[] {
      return this.filteredItems(tab).slice(tab.skip, tab.skip + tab.take);
    },

    onPageChange(tab: ExportTab, event: { page: { skip: number; take: number } }) {
      tab.skip = event.page.skip;
      tab.take = event.page.take;
    },

    selectedCount(tab: ExportTab): number {
      return tab.items.filter((i) => i.selected).length;
    },

    selectedCodes(key: ExportTab["key"]): string[] {
      const tab = this.tabs.find((t) => t.key === key);
      return tab ? tab.items.filter((i) => i.selected).map((i) => i.code) : [];
    },

    toggleItem(item: LedgerExportLookup) {
      Vue.set(item, "selected", !item.selected);
    },

    onRowClick(event: { dataItem: LedgerExportLookup }) {
      // K2D: a sorra kattintás toggle-öli a treff-jelölést
      this.toggleItem(event.dataItem);
    },

    // K2D cb_*_treff: a (szűrt) lista minden sorát kijelöli
    selectAllInTab(tab: ExportTab) {
      this.filteredItems(tab).forEach((i) => Vue.set(i, "selected", true));
    },

    // K2D cb_*_treff0: a fül összes kijelölését törli
    clearTab(tab: ExportTab) {
      tab.items.forEach((i) => Vue.set(i, "selected", false));
      tab.search = "";
      tab.skip = 0;
    },

    // K2D pb_clear: minden paraméter alaphelyzetbe — dátumok az aktuális hónapra,
    // sztornó-kapcsoló ki, minden fül kijelölése és keresője törlődik
    resetForm() {
      this.dateFrom = dayjs().startOf("month").toDate();
      this.dateTo = dayjs().endOf("month").toDate();
      this.includeStorno = false;
      this.tabs.forEach((t) => this.clearTab(t));
    },

    async exportFile() {
      if (!this.dateFrom || !this.dateTo) return;
      // K2D em_begin.modified: jövőbeli kezdő dátum nem engedett
      if (dayjs(this.dateFrom).isAfter(dayjs(), "day")) {
        eventBus.$emit("error", "A kezdő dátum nem lehet jövőbeli!");
        return;
      }
      // K2D em_end.modified: a végdátum nem lehet kisebb a kezdőnél
      if (dayjs(this.dateTo).isBefore(dayjs(this.dateFrom), "day")) {
        eventBus.$emit("error", "A végdátum nem lehet kisebb a kezdő dátumnál!");
        return;
      }

      eventBus.$emit("busy-message", true);
      try {
        const request: LedgerExportRequest = {
          dateFrom: dayjs(this.dateFrom).format("YYYY-MM-DD"),
          dateTo: dayjs(this.dateTo).format("YYYY-MM-DD"),
          includeStorno: this.includeStorno,
          accnos: this.selectedCodes("accnos"),
          dimCodes1: this.selectedCodes("dimCodes1"),
          dimCodes2: this.selectedCodes("dimCodes2"),
          dimCodes3: this.selectedCodes("dimCodes3"),
        };
        const result = await api.ExportLedgerBalances(request);
        if (!result.success || !result.fileBase64) {
          // K2D 'export_none': nincs adat -> csak üzenet, fájl nem készül
          eventBus.$emit("warn", result.message || "A megadott feltételekkel nincs exportálható adat!");
          return;
        }
        // Blob objektum-URL-lel töltünk le (MetRiportApi minta) — data-URL-nél a böngésző
        // eldobhatja a download-fájlnevet, és GUID néven menti a fájlt. A linket a DOM-ba
        // fűzzük és a revoke-ot késleltetjük, különben egyes böngészőkben szintén elveszhet a név.
        const bytes = Uint8Array.from(atob(result.fileBase64), (c) => c.charCodeAt(0));
        const url = window.URL.createObjectURL(new Blob([bytes], { type: "text/plain" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = `fokonyvi_export_${dayjs(this.dateTo).format("YYYYMMDD")}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        eventBus.$emit("success", result.message);
      } catch (e) {
        console.error(e);
        eventBus.$emit("error", "Az export sikertelen!");
      } finally {
        eventBus.$emit("busy-message", false);
      }
    },
  },
});
